import { useEffect, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import {
  ApiClientError,
  type Integration,
  type IntegrationProvider,
} from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, PasswordInput, TextInput } from "@/components/Field";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

const PROVIDERS: IntegrationProvider[] = ["openai", "anthropic", "xai", "google", "github", "gitlab"];

function statusRail(status: string): string {
  if (status === "verified") return "succeeded";
  if (status === "error") return "failed";
  if (status === "stored") return "accepted";
  return status;
}

export function IntegrationsPage() {
  const { m, locale } = useLocale();
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [name, setName] = useState("");
  const [provider, setProvider] = useState<IntegrationProvider>("openai");
  const [secret, setSecret] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);
  const [deleteAck, setDeleteAck] = useState(false);
  const [rotateId, setRotateId] = useState<string | null>(null);
  const [rotateSecret, setRotateSecret] = useState("");
  const [rotateFieldError, setRotateFieldError] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);

  async function load(opts?: { silent?: boolean }) {
    if (!opts?.silent) setLoading(true);
    try {
      const res = await api.integrations();
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next["int-name"] = m.integrations.nameRequired;
    if (secret.length < 8) next["int-secret"] = m.integrations.secretMin;
    if (secret.length > 8192) next["int-secret"] = m.integrations.secretMax;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["int-name", "int-secret"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const created = await api.createIntegration({
        name: name.trim(),
        provider,
        secret,
      });
      setName("");
      setSecret("");
      setItems((prev) => [created, ...prev.filter((i) => i.id !== created.id)]);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : m.integrations.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(id: string) {
    setActionId(id);
    setActionError(null);
    try {
      const updated = await api.verifyIntegration(id);
      setItems((prev) => prev.map((item) => (item.id === id ? updated : item)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : m.integrations.verifyFailed);
    } finally {
      setActionId(null);
    }
  }

  function openRotate(item: Integration) {
    setRotateId(item.id);
    setRotateSecret("");
    setRotateFieldError(null);
    setRotateError(null);
    setActionError(null);
  }

  function closeRotate() {
    if (actionId && rotateId === actionId) return;
    setRotateId(null);
    setRotateSecret("");
    setRotateFieldError(null);
    setRotateError(null);
  }

  async function onRotate(item: Integration) {
    if (actionId) return;
    if (rotateSecret.length < 8) {
      setRotateFieldError(m.integrations.secretMin);
      return;
    }
    if (rotateSecret.length > 8192) {
      setRotateFieldError(m.integrations.secretMax);
      return;
    }
    setRotateFieldError(null);
    setRotateError(null);
    setActionId(item.id);
    try {
      const updated = await api.rotateIntegration(item.id, {
        secret: rotateSecret,
        version: item.version,
      });
      setItems((prev) => prev.map((row) => (row.id === item.id ? updated : row)));
      setRotateSecret("");
      setRotateId(null);
    } catch (err) {
      if (err instanceof ApiClientError && (err.status === 409 || err.code === "CONFLICT")) {
        setRotateError(m.integrations.rotateConflict);
        await load({ silent: true });
      } else {
        setRotateError(err instanceof Error ? err.message : m.integrations.rotateFailed);
      }
    } finally {
      setActionId(null);
    }
  }

  async function onDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.deleteIntegration(deleteTarget.id);
      if (res.ok !== true || res.provider_key_revoked !== false) {
        setActionError(m.integrations.deleteUnexpected);
      }
      const removedId = deleteTarget.id;
      setDeleteTarget(null);
      setDeleteAck(false);
      setItems((prev) => prev.filter((item) => item.id !== removedId));
      if (rotateId === removedId) closeRotate();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : m.integrations.deleteFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthStatePanels loading={loading} error={error}>
      <div>
        <h1 className={shell.pageTitle}>{m.integrations.title}</h1>
        <p className={shell.pageLead}>{m.integrations.lead}</p>

        <form
          noValidate
          onSubmit={onCreate}
          className={ui.panel}
          style={{ display: "grid", gap: "0.75rem", marginBottom: "1.25rem" }}
          autoComplete="off"
        >
          <h2 className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
            {m.integrations.newTitle}
          </h2>
          <Field label={m.integrations.name} htmlFor="int-name" error={fieldErrors["int-name"]}>
            <TextInput
              name="integration-name"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label={m.integrations.provider} htmlFor="int-provider">
            <select
              id="int-provider"
              className={ui.input}
              value={provider}
              onChange={(e) => setProvider(e.target.value as IntegrationProvider)}
            >
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {lifecycleLabel(m, p)}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={m.integrations.secret}
            htmlFor="int-secret"
            error={fieldErrors["int-secret"]}
            hint={m.integrations.secretHint}
          >
            <PasswordInput
              name="integration-secret"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </Field>
          {formError ? (
            <p className={ui.fieldError} role="alert">
              {formError}
            </p>
          ) : null}
          <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
            {busy ? m.common.saving : m.integrations.save}
          </Button>
        </form>

        <div className={ui.panel} style={{ marginBottom: "1.25rem" }}>
          <h2 className="display" style={{ fontSize: "1.05rem", marginTop: 0 }}>
            {m.integrations.boundsTitle}
          </h2>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "var(--color-ink-secondary)" }}>
            <li>{m.integrations.bound1}</li>
            <li>{m.integrations.bound2}</li>
            <li>{m.integrations.bound3}</li>
            <li>{m.integrations.bound4}</li>
          </ul>
        </div>

        {items.length === 0 ? (
          <StatePanel kind="empty" title={m.states.empty} message={m.integrations.empty} />
        ) : (
          items.map((item) => {
            const rotating = rotateId === item.id;
            const rowBusy = actionId === item.id;
            return (
              <div key={item.id} className={ui.row} style={{ cursor: "default" }}>
                <StatusRail state={statusRail(item.status)} />
                <div style={{ width: "100%" }}>
                  <strong>{item.name}</strong>
                  <div className={ui.meta}>
                    {lifecycleLabel(m, item.provider)} · {lifecycleLabel(m, item.status)}
                    {item.last_checked_at
                      ? ` · ${m.integrations.lastChecked} ${formatWhen(item.last_checked_at, locale)}`
                      : ""}
                  </div>
                  <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.55rem" }}>
                    <Button
                      variant="secondary"
                      disabled={Boolean(actionId)}
                      onClick={() => void onVerify(item.id)}
                    >
                      {rowBusy && !rotating ? m.integrations.verifying : m.integrations.verify}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={Boolean(actionId) && !rotating}
                      onClick={() => (rotating ? closeRotate() : openRotate(item))}
                    >
                      {rotating ? m.common.cancel : m.integrations.rotate}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={Boolean(actionId) || busy}
                      onClick={() => {
                        setDeleteAck(false);
                        setDeleteTarget(item);
                      }}
                    >
                      {m.integrations.delete}
                    </Button>
                  </div>
                  {rotating ? (
                    <div style={{ display: "grid", gap: "0.65rem", marginTop: "0.75rem" }}>
                      <p className={ui.meta}>{m.integrations.rotateHint}</p>
                      <Field
                        label={m.integrations.newSecret}
                        htmlFor={`int-rotate-${item.id}`}
                        error={rotateFieldError ?? undefined}
                      >
                        <PasswordInput
                          name={`integration-rotate-${item.id}`}
                          autoComplete="new-password"
                          value={rotateSecret}
                          disabled={rowBusy}
                          onChange={(e) => setRotateSecret(e.target.value)}
                        />
                      </Field>
                      {rotateError ? (
                        <p className={ui.fieldError} role="alert">
                          {rotateError}
                        </p>
                      ) : null}
                      <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
                        <Button
                          type="button"
                          disabled={rowBusy || rotateSecret.length === 0}
                          aria-busy={rowBusy || undefined}
                          onClick={() => void onRotate(item)}
                        >
                          {rowBusy ? m.integrations.rotating : m.integrations.rotateSave}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        {actionError ? <p className={ui.fieldError}>{actionError}</p> : null}

        <ConfirmSheet
          open={Boolean(deleteTarget)}
          title={m.integrations.deleteTitle}
          confirmLabel={m.integrations.deleteConfirm}
          danger
          confirmDisabled={!deleteAck}
          busy={busy}
          onClose={() => {
            setDeleteTarget(null);
            setDeleteAck(false);
          }}
          onConfirm={() => void onDelete()}
        >
          <p>{m.integrations.deleteBody}</p>
          <label style={{ display: "flex", gap: "0.55rem" }}>
            <input
              type="checkbox"
              checked={deleteAck}
              onChange={(e) => setDeleteAck(e.target.checked)}
            />
            <span>{m.integrations.deleteAck}</span>
          </label>
        </ConfirmSheet>
      </div>
    </AuthStatePanels>
  );
}
